import "./index.scss";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Props:
 * - options: Array<{ value:any, label:string }> | Array<string>
 * - value: any | any[] (quando multiple=true, usar array)
 * - onChange:
 *      - single: (value, option)
 *      - multiple: (valuesArray, optionsArray)
 * - multiple: boolean (default: false)
 * - maxSelected: number (limite em múltipla, default: Infinity)
 * - placeholder: string
 * - searchable: boolean
 * - searchPlaceholder: string
 * - noResultsText: string
 * - className: string
 * - disabled: boolean
 * - closeOnSelect: boolean (default: !multiple)
 */
export default function InputSelect({
  options = [],
  value = null,
  title,
  subspan = "",
  onChange,
  multiple = false,
  maxSelected = Infinity,
  placeholder = "Selecione...",
  searchable = false,
  searchPlaceholder = "Pesquisar...",
  noResultsText = "Sem resultados",
  className = "",
  disabled = false,
  closeOnSelect: closeOnSelectProp,
}) {
  const closeOnSelect = closeOnSelectProp ?? !multiple;

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const normalized = useMemo(
    () =>
      options.map((o) =>
        typeof o === "string" ? { value: o, label: o } : { ...o }
      ),
    [options]
  );
  const selectedValues = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : [];
    return value === null || value === undefined ? [] : [value];
  }, [value, multiple]);
  const selectedOptions = useMemo(
    () => normalized.filter((o) => selectedValues.includes(o.value)),
    [normalized, selectedValues]
  );
  const selectedSingle = !multiple ? selectedOptions[0] || null : null;
  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return normalized;
    return normalized.filter((o) => o.label.toLowerCase().includes(t));
  }, [term, normalized]);

  useEffect(() => {
    function onDocMouseDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function toggle() {
    if (disabled) return;
    setOpen((s) => !s);
    if (!open) setTerm("");
  }
  function handleSelect(opt) {
    if (!multiple) {
      onChange?.(opt.value, opt);
      if (closeOnSelect) setOpen(false);
      return;
    }

    const exists = selectedValues.includes(opt.value);
    let nextValues;
    if (exists) {
      nextValues = selectedValues.filter((v) => v !== opt.value);
    } else {
      if (selectedValues.length >= maxSelected) return;
      nextValues = [...selectedValues, opt.value];
    }
    const nextOptions = normalized.filter((o) => nextValues.includes(o.value));
    onChange?.(nextValues, nextOptions);
    if (closeOnSelect) setOpen(false);
  }
  function renderButtonText() {
    if (!multiple) {
      return selectedSingle ? selectedSingle.label : placeholder;
    }
    if (selectedOptions.length === 0) return placeholder;
    const MAX_CHIPS = 2;
    const names = selectedOptions.map((o) => o.label);
    const shown = names.slice(0, MAX_CHIPS).join(", ");
    const rest = names.length - MAX_CHIPS;
    return rest > 0 ? `${shown} (+${rest})` : shown;
  }

  return (
    <div className="input-select-container">
      <p className="input-select-title">{title}</p>
      <div ref={wrapRef} className={`input-select relative ${className}`}>
        <button
          ref={btnRef}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
          className={`input-select__button
          ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
          ${open ? "open" : ""}
        `}
        >
          <span
            className={`${
              selectedOptions.length ? "" : "input-select__placeholder"
            }`}
          >
            {renderButtonText()}
          </span>

          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3 6L8 11L13 6H3Z"
              fill="#2E5DE6"
            />
          </svg>
        </button>

        {open && (
          <div className="input-select__options" role="listbox" ref={listRef}>
            {searchable && (
              <div className="wrapper-input">
                <label htmlFor="input-select__search" className="input w-full">
                  <input
                    type="search"
                    id="input-select__search"
                    autoComplete="off"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder={searchPlaceholder}
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <g
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      strokeWidth="2.5"
                      fill="none"
                      stroke="currentColor"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.3-4.3"></path>
                    </g>
                  </svg>
                </label>
              </div>
            )}

            <ul className="">
              {/* <ul className="max-h-45 overflow-auto"> */}
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-slate-500">{noResultsText}</li>
              ) : (
                filtered.map((opt) => {
                  const isSelected = selectedValues.includes(opt.value);
                  return (
                    <li key={String(opt.value)}>
                      <button
                        type="button"
                        onClick={() => handleSelect(opt)}
                        className={`input-select__item ${
                          !multiple && isSelected ? "selected" : ""
                        } hover:bg-blue-50`}
                        aria-selected={isSelected}
                      >
                        {multiple && (
                          <span
                            className={`mr-2 inline-flex h-4 w-4 items-center justify-center rounded border ${
                              isSelected
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "border-slate-300"
                            }`}
                          >
                            {isSelected ? (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z" />
                                </svg>
                              </>
                            ) : (
                              ""
                            )}
                          </span>
                        )}
                        {opt.label} <span className="subspan">{opt.subspan ?? subspan ?? ""}</span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            {multiple && isFinite(maxSelected) && (
              <div className="px-4 py-2 text-xs text-slate-500">
                {selectedValues.length}/{maxSelected} selecionado(s)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
